import { useState, useEffect, useContext, useRef } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  MessageCircle, Send, Users, Crown, Smile, AtSign,
  Clock, Shield, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const ChatRoomPage = () => {
  const { user, token } = useContext(UserContext);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [onlineCount, setOnlineCount] = useState(Math.floor(Math.random() * 100) + 50);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 3 seconds
    const interval = setInterval(fetchMessages, 3000);
    
    // Simulate online count changes
    const onlineInterval = setInterval(() => {
      setOnlineCount(prev => prev + Math.floor(Math.random() * 5) - 2);
    }, 10000);
    
    return () => {
      clearInterval(interval);
      clearInterval(onlineInterval);
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/messages?limit=50`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    if (newMessage.length > 500) {
      toast.error("Message too long (max 500 characters)");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/chat/send`, {
        message: newMessage
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const vipColors = {
    1: "#CD7F32", 2: "#C0C0C0", 3: "#FFD700",
    4: "#E5E4E2", 5: "#B9F2FF", 6: "#FF007A"
  };

  const vipNames = {
    1: "Bronze", 2: "Silver", 3: "Gold",
    4: "Platinum", 5: "Diamond", 6: "Elite"
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#007AFF] to-[#00E701] flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Rajdhani']">Chat Room</h1>
              <p className="text-[#A1A1AA] text-sm">Chat with other players</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#00E701]/10 border border-[#00E701]/30">
            <div className="w-2 h-2 rounded-full bg-[#00E701] animate-pulse" />
            <span className="text-[#00E701] text-sm font-medium">{onlineCount} Online</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Chat Window */}
          <div className="lg:col-span-3 flex flex-col h-[600px] rounded-2xl bg-[#1A1B1F] border border-white/10 overflow-hidden">
            {/* Chat Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#007AFF]" />
                <span className="text-white font-medium">General Chat</span>
              </div>
              <Badge className="bg-white/10 text-white/60 border-0">
                <Shield className="w-3 h-3 mr-1" />
                Moderated
              </Badge>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-4 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="w-12 h-12 text-white/10 mx-auto mb-3" />
                  <p className="text-[#A1A1AA]">No messages yet. Be the first to say hi!</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div 
                    key={msg.message_id}
                    className={`flex gap-3 ${msg.user_id === user?.user_id ? 'flex-row-reverse' : ''}`}
                  >
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                      style={{ 
                        backgroundColor: `${vipColors[msg.vip_level || 1]}20`,
                        color: vipColors[msg.vip_level || 1]
                      }}
                    >
                      {(msg.username || "A")[0].toUpperCase()}
                    </div>
                    
                    <div className={`flex-1 ${msg.user_id === user?.user_id ? 'text-right' : ''}`}>
                      <div className={`flex items-center gap-2 mb-1 ${msg.user_id === user?.user_id ? 'justify-end' : ''}`}>
                        <span className="text-white text-sm font-medium">{msg.username}</span>
                        <span 
                          className="text-xs px-1.5 py-0.5 rounded"
                          style={{ 
                            backgroundColor: `${vipColors[msg.vip_level || 1]}20`,
                            color: vipColors[msg.vip_level || 1]
                          }}
                        >
                          {vipNames[msg.vip_level || 1]}
                        </span>
                        <span className="text-[#52525B] text-xs">{formatTime(msg.created_at)}</span>
                      </div>
                      <div 
                        className={`inline-block p-3 rounded-2xl max-w-[80%] ${
                          msg.user_id === user?.user_id 
                            ? 'bg-[#007AFF] text-white rounded-tr-sm' 
                            : 'bg-white/5 text-white rounded-tl-sm'
                        }`}
                      >
                        <p className="text-sm break-words">{msg.message}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 h-12 bg-black/20 border-white/10 text-white"
                  disabled={sending}
                  maxLength={500}
                  data-testid="chat-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={sending || !newMessage.trim()}
                  className="h-12 w-12 bg-[#007AFF] hover:bg-[#0066DD] text-white p-0"
                  data-testid="send-btn"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-[#52525B] text-xs mt-1">{newMessage.length}/500 characters</p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Your Profile */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
              <h3 className="text-white font-bold mb-3">Your Profile</h3>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                  style={{ 
                    backgroundColor: `${vipColors[user?.vip_level || 1]}20`,
                    color: vipColors[user?.vip_level || 1]
                  }}
                >
                  {(user?.name || "A")[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-white font-medium">{user?.name || "Anonymous"}</p>
                  <p 
                    className="text-xs"
                    style={{ color: vipColors[user?.vip_level || 1] }}
                  >
                    {vipNames[user?.vip_level || 1]} VIP
                  </p>
                </div>
              </div>
            </div>

            {/* Chat Rules */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#FFD600]" />
                Chat Rules
              </h3>
              <ul className="space-y-2 text-[#A1A1AA] text-xs">
                <li className="flex items-start gap-2">
                  <span className="text-[#00E701]">•</span>
                  Be respectful to others
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E701]">•</span>
                  No spam or advertising
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E701]">•</span>
                  English only
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E701]">•</span>
                  No sharing personal info
                </li>
              </ul>
            </div>

            {/* VIP Badges */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <Crown className="w-4 h-4 text-[#FFD600]" />
                VIP Badges
              </h3>
              <div className="space-y-2">
                {Object.entries(vipNames).map(([level, name]) => (
                  <div key={level} className="flex items-center justify-between">
                    <span 
                      className="text-xs px-2 py-1 rounded"
                      style={{ 
                        backgroundColor: `${vipColors[level]}20`,
                        color: vipColors[level]
                      }}
                    >
                      {name}
                    </span>
                    <span className="text-[#52525B] text-xs">Level {level}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
